import React, { useState, useEffect } from 'react';
import { ShieldCheck, Activity, Layers, RefreshCw, Server } from 'lucide-react';
import { api } from '../services/api';
import { Card } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { Button } from '../components/common/Button';

export const AnalyticsView: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const data = await api.getGlobalAnalytics();
      setAnalyticsData(data);
    } catch (err: any) {
      console.error('Failed to load central analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      <div
        className="glass-panel"
        style={{
          padding: '24px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h1 style={{ fontSize: '1.5rem' }}>Central Analytics Database</h1>
            <Badge variant="success">PII De-Identified</Badge>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Unified cross-subscriber reporting pipeline sinking sanitized telemetry from independent tenant databases.
          </p>
        </div>

        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />}
          onClick={fetchAnalytics}
        >
          Refresh Telemetry
        </Button>
      </div>

      {/* Global Overview Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                TOTAL REGISTERED TENANTS
              </span>
              <h3 style={{ fontSize: '1.75rem', marginTop: '6px' }}>
                {analyticsData?.overview?.totalRegisteredTenants || 0}
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}>
              <Layers className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Each running in dedicated MongoDB database
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                CENTRAL DB STATUS
              </span>
              <h3 style={{ fontSize: '1.75rem', marginTop: '6px', color: 'var(--color-success)' }}>
                Healthy
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
              <Server className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            saas_central_analytics connection pool active
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                DATA SANITIZATION
              </span>
              <h3 style={{ fontSize: '1.75rem', marginTop: '6px', color: '#a855f7' }}>
                100% Strict
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7' }}>
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Zero user email / passwords / PII stored centrally
          </div>
        </Card>
      </div>

      {/* Recent Sanitized Events Stream */}
      <div className="glass-panel" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '8px' }}>Recent De-Identified Telemetry Feed</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>
          Streamed records in Central Analytics DB (<code style={{ color: '#a5b4fc' }}>TenantMetric</code> collection)
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                <th style={{ padding: '10px 12px' }}>Event Type</th>
                <th style={{ padding: '10px 12px' }}>Tenant ID</th>
                <th style={{ padding: '10px 12px' }}>Tier</th>
                <th style={{ padding: '10px 12px' }}>Category</th>
                <th style={{ padding: '10px 12px' }}>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {analyticsData?.recentDeidentifiedTelemetry?.map((evt: any) => (
                <tr key={evt.eventId} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '12px', fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Activity className="w-3.5 h-3.5" style={{ color: 'var(--brand-primary)' }} />
                      {evt.eventType}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: '#93c5fd' }}>
                    {evt.tenantId}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <Badge variant="primary">{evt.tier}</Badge>
                  </td>
                  <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                    {evt.category}
                  </td>
                  <td style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                    {new Date(evt.timestamp).toLocaleTimeString()} ({new Date(evt.timestamp).toLocaleDateString()})
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
